import React from "react";
import { Link } from "react-router-dom";
import { Star, Reveal, Label, GoldButton } from "../components/primitives";
import { VALUES } from "../data";

const PageHead = ({ label, title, sub }) => (
  <section className="max-w-7xl mx-auto px-6 md:px-12 pt-40 md:pt-52 pb-16 relative z-10">
    <Reveal><Label>{label}</Label></Reveal>
    <Reveal delay={0.1}>
      <h1 className="font-serif text-6xl md:text-8xl lg:text-9xl font-[200] leading-[0.9] mt-8">{title}</h1>
    </Reveal>
    {sub && (
      <Reveal delay={0.2}>
        <p className="text-white/50 text-lg md:text-xl max-w-2xl mt-8 leading-relaxed">{sub}</p>
      </Reveal>
    )}
  </section>
);

export default function About() {
  return (
    <div data-testid="about-page">
      <PageHead
        label="Our story"
        title={<>God is<br /><span className="italic text-gold">Good.</span></>}
        sub="Yahtov — יהטוב — means &ldquo;God is Good&rdquo; in Hebrew. It is more than a name; it is the foundation of everything we make."
      />

      {/* Story + portrait */}
      <section className="max-w-7xl mx-auto px-6 md:px-12 py-24 relative z-10">
        <div className="grid md:grid-cols-12 gap-12 lg:gap-20 items-center">
          <div className="md:col-span-5">
            <Reveal>
              <div className="relative">
                <div className="aspect-[4/5] overflow-hidden border hairline">
                  <img
                    src="/media/gregory.png"
                    alt="Gregory Foltran, Founder of Yahtov"
                    className="w-full h-full object-cover"
                  />
                </div>
                <div className="absolute -bottom-6 -right-6 bg-[#0D1220] border hairline px-6 py-4">
                  <p className="font-serif text-2xl">Gregory Foltran</p>
                  <p className="label text-gold mt-1">Founder &middot; Matthews, NC</p>
                </div>
              </div>
            </Reveal>
          </div>
          <div className="md:col-span-7">
            <Reveal delay={0.1}>
              <p className="font-serif text-3xl md:text-5xl font-[300] leading-[1.2]">
                We believe good work is <span className="text-gold italic">an act of worship.</span>
              </p>
              <div className="mt-10 space-y-6 text-white/55 text-lg leading-relaxed max-w-xl">
                <p>
                  Yahtov was founded in Charlotte, North Carolina on a simple conviction: that the
                  way we work is a reflection of Who we serve. Excellence isn't vanity — it's reverence.
                </p>
                <p>
                  From photography and film to design and the web, we partner with businesses and
                  people who want their brand to be built with integrity, beauty, and heart. Every
                  project is stewarded with care, as though it were made for the Lord Himself.
                </p>
              </div>
            </Reveal>
          </div>
        </div>
      </section>

      {/* Values — numbered manifesto */}
      <section className="border-t hairline relative z-10 py-24 md:py-32">
        <div className="max-w-7xl mx-auto px-6 md:px-12">
          <Reveal><Label>What we believe</Label></Reveal>
          <div className="mt-16 grid md:grid-cols-2 gap-px bg-[#D4A843]/15 border hairline">
            {VALUES.map((v, i) => (
              <Reveal key={v.n} delay={(i % 2) * 0.1}>
                <div data-testid={`value-${v.n}`} className="bg-[#0D1220] p-10 md:p-16 relative overflow-hidden group">
                  <span className="absolute -top-6 -right-2 font-serif text-[10rem] leading-none text-[#D4A843]/10 select-none">{v.n}</span>
                  <div className="relative">
                    <Star size={14} className="text-gold mb-6" />
                    <h3 className="font-serif text-4xl md:text-5xl font-[300] mb-4">{v.title}</h3>
                    <p className="text-white/50 leading-relaxed max-w-md">{v.desc}</p>
                  </div>
                </div>
              </Reveal>
            ))}
          </div>
        </div>
      </section>

      {/* CTA */}
      <section className="py-32 text-center px-6 relative z-10 border-t hairline">
        <Reveal>
          <h2 className="font-serif text-5xl md:text-7xl font-[200]">Work with intention.</h2>
          <div className="mt-12 flex justify-center">
            <Link to="/contact"><GoldButton data-testid="about-cta">Let's work together</GoldButton></Link>
          </div>
        </Reveal>
      </section>
    </div>
  );
}
